/**
 * useFocusTrap — manages focus inside a panel or dialog.
 *
 * Handles:
 *  1. Moves focus into the container when isActive becomes true.
 *  2. Traps Tab key cycling within the container while active.
 *  3. Calls onEscape when the user presses ESC while active, unless a
 *     handler inside the panel already claimed it (`preventDefault()`).
 *
 * Pass a guarded onEscape when the caller has its own dialog layered on top
 * (e.g. SlidePanel passes a version that skips the call when DiscardChangesDialog
 * is already open, preventing a double-trigger).
 *
 * Multiple panels can be active at once (a SlidePanel opened from inside
 * another, e.g. the Data Risks drawer over the Overview panel). `activeStack`
 * tracks every currently-active ref in mount order so the shared `keydown`
 * listener below can tell which panel is frontmost — Escape and the Tab trap
 * only act on the top of the stack, otherwise one Escape would close every
 * nested panel at once. Removal is by identity, not `pop()`, since panels can
 * unmount out of order while a sibling's slide-out transition is still
 * playing.
 */
import React, { useEffect } from "react";
import { getFocusable, hasOpenDialog } from "@/helpers/focus";

const activeStack: React.RefObject<HTMLElement | null>[] = [];

export function useFocusTrap(
  ref: React.RefObject<HTMLElement | null>,
  isActive: boolean,
  onEscape: () => void,
): void {
  // 1. Move initial focus into the container on open
  useEffect(() => {
    if (!isActive) return;
    const frame = requestAnimationFrame(() => {
      getFocusable(ref.current)[0]?.focus();
    });
    return () => cancelAnimationFrame(frame);
  }, [isActive, ref]);

  // Track this panel's place in the stack of currently-active panels.
  useEffect(() => {
    if (!isActive) return;
    activeStack.push(ref);
    return () => {
      const index = activeStack.indexOf(ref);
      if (index !== -1) activeStack.splice(index, 1);
    };
  }, [isActive, ref]);

  // 2. Tab trap + 3. ESC key
  useEffect(() => {
    if (!isActive) return;
    const handler = (e: KeyboardEvent) => {
      if (activeStack[activeStack.length - 1] !== ref) return;
      // A Kendo Dialog/Modal (e.g. RemediationStatus's error details) portals
      // to document.body outside `ref.current` and manages its own focus —
      // defer to it entirely so Escape/Tab here don't fight its own handlers
      // or reach past it to close the panel underneath.
      if (hasOpenDialog()) return;
      if (e.key === "Escape") {
        // Something closer to the reader already acted on it (a popup inside
        // the panel, a full-screen workspace) — one Escape, one step back.
        if (e.defaultPrevented) return;
        e.preventDefault();
        onEscape();
        return;
      }
      if (e.key !== "Tab") return;
      const focusable = getFocusable(ref.current);
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [isActive, ref, onEscape]);
}
